// =============================================================================
// components/ai/R2ChatPanel.tsx  ·  R2 Persistent AI Chat Panel
// Enterprise engineering AI assistant — always present in the layout
// =============================================================================

import {
  useState, useEffect, useRef, useCallback, type JSX, type KeyboardEvent,
} from 'react';
import {
  MessageSquare, X, Minimize2, Maximize2, Send, Mic, MicOff, Paperclip,
  ChevronRight, ChevronDown, Box, FileText, AlertTriangle, Calendar, Users,
  Zap, MoreHorizontal, RefreshCw, Trash2, Copy, Check, Bot,
  LayoutGrid, Activity, Layers, ClipboardCheck, Navigation, History, Radio,
} from 'lucide-react';
import { useR2ChatStore, type ChatMessage, type ActionSuggestion } from '../../stores/r2ChatStore.js';
import { resolveGhostSuggestion, type GhostSuggestion } from './ghostSuggestions.js';
import { R2AIAssistHistory } from './R2AIAssistHistory.js';
import { R2VoiceAssistant } from '../voice/R2VoiceAssistant.js';
import type { Route } from '../../App.js';
import { FloatingWindowFrame } from '../windowing/FloatingWindowFrame.js';

const API = import.meta.env.VITE_API_URL ?? (typeof process !== 'undefined' ? (process.env?.REACT_APP_BACKEND_URL ?? '') : '');

// ─── Action icons ─────────────────────────────────────────────────────────────
const ACTION_META: Record<string, { icon: JSX.Element; color: string }> = {
  navigate_to_viewer:   { icon: <Box size={11}/>,            color: '#5eead4' },
  create_task:          { icon: <ClipboardCheck size={11}/>, color: '#60a5fa' },
  generate_report:      { icon: <FileText size={11}/>,       color: '#34d399' },
  create_rfi:           { icon: <AlertTriangle size={11}/>,  color: '#f59e0b' },
  open_meeting:         { icon: <Calendar size={11}/>,       color: '#a78bfa' },
  view_documents:       { icon: <FileText size={11}/>,       color: '#94a3b8' },
  run_clash_detection:  { icon: <Activity size={11}/>,       color: '#f87171' },
  open_commissioning:   { icon: <Layers size={11}/>,         color: '#60a5fa' },
  view_annotations:     { icon: <LayoutGrid size={11}/>,     color: '#fbbf24' },
  open_mission_control: { icon: <Navigation size={11}/>,     color: '#5eead4' },
};

// Starter messages
const STARTER_PROMPTS = [
  "Show me all telecom rooms on Level 3",
  "What are the open RFIs for this project?",
  "Analyze the current commissioning status",
  "Summarize the last coordination meeting",
  "What clash issues exist in the MDF room?",
  "Generate a handover checklist",
];

// ─── Context Bar Items ────────────────────────────────────────────────────────
function ContextBar(): JSX.Element {
  const { contextSummary, context } = useR2ChatStore();
  const u = contextSummary?.understanding ?? {};

  const items = [
    { label: '3D Model',    active: u.model     ?? true,  icon: <Box size={10}/> },
    { label: 'Documents',   active: u.documents ?? false, icon: <FileText size={10}/> },
    { label: 'RFIs',        active: u.rfis      ?? false, icon: <AlertTriangle size={10}/> },
    { label: 'Meetings',    active: u.meetings  ?? false, icon: <Calendar size={10}/> },
    { label: 'Equipment',   active: u.equipment ?? true,  icon: <Zap size={10}/> },
    { label: 'Drawings',    active: u.drawings  ?? false, icon: <Layers size={10}/> },
  ];

  return (
    <div style={{
      padding: '10px 14px', borderBottom: '1px solid rgba(37,181,168,0.1)',
      background: 'rgba(2,6,16,0.8)',
    }}>
      <div style={{ fontSize: 8, color: 'rgba(37,181,168,0.45)', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, marginBottom: 6 }}>
        R2 CONTEXT AWARENESS
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {items.map(({ label, active, icon }) => (
          <div
            key={label}
            title={active ? `R2 understands ${label}` : `${label} not yet indexed`}
            style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '2px 7px',
              borderRadius: 5, fontSize: 9, fontWeight: 600,
              background: active ? 'rgba(37,181,168,0.1)' : 'rgba(30,40,60,0.3)',
              border: `1px solid ${active ? 'rgba(37,181,168,0.3)' : 'rgba(50,60,80,0.2)'}`,
              color: active ? '#5eead4' : 'rgba(100,120,150,0.35)',
              cursor: 'default',
            }}
          >
            {icon}
            {active && <span style={{ color: '#4ade80', fontSize: 8 }}>✓</span>}
            {label}
          </div>
        ))}
      </div>
      {context.selected_object && (
        <div style={{
          marginTop: 6, display: 'flex', alignItems: 'center', gap: 6,
          padding: '4px 8px', borderRadius: 6, background: 'rgba(94,234,212,0.06)',
          border: '1px solid rgba(94,234,212,0.2)',
        }}>
          <Box size={9} style={{ color: '#5eead4' }} />
          <span style={{ fontSize: 9, color: '#5eead4', fontWeight: 600 }}>
            Selected: {(context.selected_object as any)?.name ?? 'Object'}
          </span>
          <span style={{ fontSize: 8, color: 'rgba(94,234,212,0.4)', marginLeft: 2 }}>
            {(context.selected_object as any)?.discipline?.toUpperCase()} · {(context.selected_object as any)?.level}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Message Bubble ───────────────────────────────────────────────────────────
function MessageBubble({
  msg, onAction, onCopy,
}: {
  msg: ChatMessage;
  onAction: (action: string) => void;
  onCopy: (text: string) => void;
}): JSX.Element {
  const isUser = msg.role === 'user';
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    onCopy(msg.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Render markdown-like text (bold, bullets)
  const renderContent = (text: string) => {
    const lines = text.split('\n');
    return lines.map((line, i) => {
      if (line.startsWith('• ') || line.startsWith('- ')) {
        return (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
            <span style={{ color: '#5eead4', flexShrink: 0, marginTop: 1 }}>·</span>
            <span>{line.slice(2)}</span>
          </div>
        );
      }
      if (line.match(/^\d+\./)) {
        return <div key={i} style={{ marginBottom: 2 }}>{line}</div>;
      }
      if (line === '') return <div key={i} style={{ height: 6 }} />;
      // Bold text between **
      const parts = line.split(/\*\*([^*]+)\*\*/g);
      return (
        <div key={i} style={{ marginBottom: 1 }}>
          {parts.map((part, j) =>
            j % 2 === 1
              ? <strong key={j} style={{ color: '#e2e8f0', fontWeight: 700 }}>{part}</strong>
              : part
          )}
        </div>
      );
    });
  };

  return (
    <div
      data-testid={`chat-msg-${msg.id}`}
      style={{
        display: 'flex', gap: 8, marginBottom: 14,
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
      }}
    >
      {/* Avatar */}
      {!isUser && (
        <div style={{
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
          background: 'rgba(37,181,168,0.12)', border: '1px solid rgba(37,181,168,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2,
        }}>
          <Bot size={13} style={{ color: '#5eead4' }} />
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Bubble */}
        <div style={{
          padding: '10px 13px',
          borderRadius: isUser ? '14px 4px 14px 14px' : '4px 14px 14px 14px',
          background: isUser
            ? 'linear-gradient(135deg, rgba(37,181,168,0.18), rgba(37,181,168,0.08))'
            : 'rgba(12,22,44,0.9)',
          border: isUser
            ? '1px solid rgba(37,181,168,0.3)'
            : '1px solid rgba(30,50,80,0.5)',
          fontSize: 12.5,
          lineHeight: 1.6,
          color: isUser ? '#e2f4f2' : '#c8d8f0',
          fontFamily: 'sans-serif',
          position: 'relative',
          maxWidth: '92%',
          marginLeft: isUser ? 'auto' : 0,
        }}>
          {renderContent(msg.content)}
          {msg.isStreaming && (
            <span style={{
              display: 'inline-block', width: 7, height: 13, marginLeft: 3,
              background: '#5eead4', borderRadius: 1,
              animation: 'r2-cursor 1s ease-in-out infinite',
              verticalAlign: 'text-bottom',
            }} />
          )}
        </div>

        {/* Action buttons */}
        {!isUser && msg.actions && msg.actions.length > 0 && !msg.isStreaming && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7 }}>
            {msg.actions.map((a) => {
              const meta = ACTION_META[a.action] ?? { icon: <Zap size={11}/>, color: '#5eead4' };
              return (
                <button
                  key={a.action}
                  data-testid={`chat-action-${a.action}`}
                  onClick={() => onAction(a.action)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
                    borderRadius: 7, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                    background: `${meta.color}10`, border: `1px solid ${meta.color}30`,
                    color: meta.color, transition: 'all 0.12s', fontFamily: 'inherit',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = `${meta.color}18`; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = `${meta.color}10`; }}
                >
                  {meta.icon} {a.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Meta row */}
        {!isUser && !msg.isStreaming && (
          <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
            <span style={{ fontSize: 9, color: 'rgba(100,120,150,0.35)' }}>
              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
            <button
              onClick={handleCopy}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '1px 4px', borderRadius: 4, color: copied ? '#4ade80' : 'rgba(100,120,150,0.35)', display: 'flex', alignItems: 'center', gap: 3, fontSize: 9 }}
            >
              {copied ? <Check size={9}/> : <Copy size={9}/>}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────
interface R2ChatPanelProps {
  onNavigate: (route: string) => void;
  currentRoute?: string;
}

export function R2ChatPanel({ onNavigate, currentRoute }: R2ChatPanelProps): JSX.Element {
  const {
    isOpen, isExpanded, isMinimized, sessionId,
    messages, isStreaming, context,
    openPanel, closePanel, togglePanel, toggleExpand,
    setSessionId, addMessage, updateStreamingMessage, finalizeMessage,
    setStreaming, updateContext, setContextSummary, setListening, isListening,
    clearMessages,
  } = useR2ChatStore();

  const [input, setInput]       = useState('');
  const [showStarters, setShowStarters] = useState(true);
  const [ghostSuggestion, setGhostSuggestion] = useState<GhostSuggestion | null>(null);
  const [activeTab, setActiveTab] = useState<'chat' | 'history' | 'voice'>('chat');
  const messagesEndRef  = useRef<HTMLDivElement>(null);
  const inputRef        = useRef<HTMLTextAreaElement>(null);
  const recognitionRef  = useRef<any>(null);

  // Panel width
  const panelWidth = isExpanded ? 640 : 420;

  // ── Ghost suggestion — computed on every input change ────────────────────
  useEffect(() => {
    if (!input || input.length === 0) {
      const suggestion = resolveGhostSuggestion('', currentRoute ?? 'dashboard', null);
      setGhostSuggestion(suggestion);
      return;
    }
    const selectedDiscipline = context.selected_object
      ? (context.selected_object as any)?.discipline ?? null
      : null;
    const suggestion = resolveGhostSuggestion(input, currentRoute ?? 'dashboard', selectedDiscipline);
    setGhostSuggestion(suggestion);
  }, [input, currentRoute, context.selected_object]);

  // ── Init session ──────────────────────────────────────────────────────────
  useEffect(() => {
    const initSession = async () => {
      try {
        const res = await fetch(`${API}/api/r2-chat/sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            project_name: 'ABC Tower',
            building_name: '100 Storey Mixed Use Tower',
          }),
        });
        if (res.ok) {
          const { session_id } = await res.json();
          setSessionId(session_id);
        }
      } catch { /* ignore */ }
    };

    const loadContextSummary = async () => {
      try {
        const res = await fetch(`${API}/api/r2-chat/context-summary`);
        if (res.ok) setContextSummary(await res.json());
      } catch { /* ignore */ }
    };

    initSession();
    loadContextSummary();
  }, []);

  // ── Track current page context ────────────────────────────────────────────
  useEffect(() => {
    if (currentRoute) updateContext({ current_page: currentRoute });
  }, [currentRoute]);

  // ── Listen for BIM object selection from SmartViewer ─────────────────────
  useEffect(() => {
    const handler = (e: Event) => {
      const { object } = (e as CustomEvent).detail;
      if (object) {
        updateContext({ selected_object: object });
        // Auto-send a brief note to R2 about the selection
        if (isOpen && messages.length > 0) {
          handleSend(`I selected: ${object.name} (${object.discipline} · ${object.level})`);
        }
      } else {
        updateContext({ selected_object: null });
      }
    };
    window.addEventListener('r2:viewer:object-selected', handler);
    return () => window.removeEventListener('r2:viewer:object-selected', handler);
  }, [isOpen, messages.length]);

  // ── Listen for quick commands from PIE and other modules ──────────────────
  useEffect(() => {
    const handler = (e: Event) => {
      const { text } = (e as CustomEvent).detail;
      if (text) {
        openPanel();
        setTimeout(() => handleSend(text), 100);
      }
    };
    window.addEventListener('r2:chat:send', handler);
    return () => window.removeEventListener('r2:chat:send', handler);
  }, [openPanel]);

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Hide starters once user sends a message, show again when cleared ─────
  useEffect(() => {
    if (messages.length === 0) setShowStarters(true);
    else setShowStarters(false);
  }, [messages.length]);

  // ── Send message ──────────────────────────────────────────────────────────
  const handleSend = useCallback(async (textOverride?: string) => {
    const text = (textOverride ?? input).trim();
    if (!text || isStreaming || !sessionId) return;
    if (!textOverride) setInput('');

    const userMsgId = `u-${Date.now()}`;
    const botMsgId  = `b-${Date.now()}`;
    const now       = new Date().toISOString();

    addMessage({ id: userMsgId, role: 'user', content: text, created_at: now });
    addMessage({ id: botMsgId, role: 'assistant', content: '', created_at: now, isStreaming: true });
    setStreaming(true, botMsgId);

    try {
      const res = await fetch(`${API}/api/r2-chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message:    text,
          context: {
            project_name:    context.project_name,
            building_name:   context.building_name,
            current_page:    context.current_page,
            selected_object: context.selected_object,
            open_rfis:       context.open_rfis,
            open_tasks:      context.open_tasks,
          },
        }),
      });

      if (!res.ok || !res.body) throw new Error('Stream failed');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';
      let finalActions: ActionSuggestion[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';

        for (const part of parts) {
          if (part.startsWith('data: ')) {
            try {
              const data = JSON.parse(part.slice(6));
              if (data.delta) {
                fullContent += data.delta;
                updateStreamingMessage(botMsgId, data.delta);
              }
              if (data.done) {
                finalActions = data.actions ?? [];
                const cleanContent = data.clean_content ?? fullContent;
                finalizeMessage(botMsgId, cleanContent, finalActions);
              }
            } catch { /* skip */ }
          }
        }
      }
    } catch (err) {
      finalizeMessage(botMsgId, 'I\'m temporarily unavailable. Please try again.', []);
    } finally {
      setStreaming(false);
    }
  }, [input, isStreaming, sessionId, context, addMessage, updateStreamingMessage, finalizeMessage, setStreaming]);

  // ── Handle action buttons ─────────────────────────────────────────────────
  const handleAction = useCallback((action: string) => {
    const routeMap: Record<string, string> = {
      navigate_to_viewer:   'r2-smart-viewer',
      create_task:          'r2-ctdte',
      generate_report:      'r2-ctdte',
      create_rfi:           'r2-meeting-intelligence',
      open_meeting:         'r2-meeting-intelligence',
      view_documents:       'documents',
      run_clash_detection:  'clashes',
      open_commissioning:   'r2-ctdte',
      view_annotations:     'r2-annotations',
      open_mission_control: 'r2-mission-control',
    };
    const route = routeMap[action];
    if (route) {
      onNavigate(route);
      closePanel();
    }
  }, [onNavigate, closePanel]);

  // ── Copy ──────────────────────────────────────────────────────────────────
  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  }, []);

  // ── Voice input ──────────────────────────────────────────────────────────
  const toggleVoice = useCallback(() => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      setInput('Voice not supported in this browser.');
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition: any = new SpeechRecognition();
    recognition.continuous = false;
    recognition.lang = 'en-US';
    recognition.onresult = (e: any) => {
      const transcript = Array.from(e.results).map((r: any) => r[0].transcript).join(' ');
      setInput(transcript);
      setListening(false);
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }, [isListening, setListening]);

  // ── Keyboard shortcut (Ctrl+Shift+R) to toggle panel ─────────────────────
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'R') {
        e.preventDefault();
        togglePanel();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [togglePanel]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab' && ghostSuggestion) {
      e.preventDefault();
      setInput(ghostSuggestion.text);
      setGhostSuggestion(null);
      return;
    }
    if (e.key === 'Escape' && ghostSuggestion) {
      e.preventDefault();
      setGhostSuggestion(null);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      setGhostSuggestion(null);
      handleSend();
    }
  };

  // ─── Render collapsed state ────────────────────────────────────────────────
  if (!isOpen || isMinimized) {
    return (
      <button
        data-testid="r2-chat-toggle-btn"
        onClick={openPanel}
        title="R2 AI Assistant (Ctrl+Shift+R)"
        style={{
          position: 'fixed', right: 20, bottom: 24, zIndex: 99700,
          width: 52, height: 52, borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(37,181,168,0.2), rgba(37,181,168,0.08))',
          border: '1.5px solid rgba(37,181,168,0.45)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 24px rgba(37,181,168,0.18), 0 0 0 1px rgba(37,181,168,0.08)',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1.08)';
          (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 32px rgba(37,181,168,0.28), 0 0 0 1px rgba(37,181,168,0.15)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
          (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 24px rgba(37,181,168,0.18), 0 0 0 1px rgba(37,181,168,0.08)';
        }}
      >
        <Bot size={22} style={{ color: '#5eead4' }} />
        {isStreaming && (
          <div style={{
            position: 'absolute', top: -3, right: -3, width: 12, height: 12,
            borderRadius: '50%', background: '#5eead4',
            animation: 'r2-pulse 1.2s ease-in-out infinite',
          }} />
        )}
      </button>
    );
  }

  // ─── Render open panel ─────────────────────────────────────────────────────
  return (
    <>
      {/* Panel */}
      <FloatingWindowFrame
        id="r2-ai-assistant"
        title="R2 AI Assistant"
        group="Intelligence"
        defaultRect={{ x: Math.max(20, window.innerWidth - panelWidth - 18), y: 70, width: panelWidth, height: Math.max(520, window.innerHeight - 124) }}
        canMaximize
        keyboardClosable
        important
        accent="#5eead4"
        contentStyle={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onClose={closePanel}
        onMinimize={() => useR2ChatStore.setState({ isOpen: true, isMinimized: true })}
        onRestore={openPanel}
        onOpen={openPanel}
      >
      <div
        data-testid="r2-chat-panel"
        style={{
          width: '100%', height: '100%',
          background: 'rgba(2,6,18,0.97)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Top accent */}
        <div style={{ height: 2, background: 'linear-gradient(90deg, transparent, #25B5A8 40%, #818cf8 80%, transparent)', flexShrink: 0 }} />

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px 10px',
          borderBottom: '1px solid rgba(37,181,168,0.1)', flexShrink: 0,
          background: 'rgba(2,5,16,0.85)',
        }}>
          {/* Logo + title */}
          <div style={{
            width: 32, height: 32, borderRadius: 9,
            background: 'linear-gradient(135deg, rgba(37,181,168,0.2), rgba(37,181,168,0.06))',
            border: '1px solid rgba(37,181,168,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Bot size={16} style={{ color: '#5eead4' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#f0f4ff', letterSpacing: '-0.01em' }}>R2 AI Assistant</div>
            <div style={{ fontSize: 9, color: 'rgba(94,234,212,0.5)', fontWeight: 600, letterSpacing: '0.06em' }}>
              {context.project_name ?? 'No Project'} · {context.building_name ?? ''}
            </div>
          </div>
          {/* Status dot */}
          <div style={{
            width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
            background: isStreaming ? '#fbbf24' : '#4ade80',
            boxShadow: `0 0 6px ${isStreaming ? '#fbbf2480' : '#4ade8080'}`,
            animation: isStreaming ? 'r2-pulse 1s ease-in-out infinite' : 'none',
          }} />
          {/* Controls */}
          <div style={{ display: 'flex', gap: 2 }}>
            <button
              data-testid="r2-chat-clear-btn"
              onClick={clearMessages}
              title="Clear conversation"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 6, color: 'rgba(100,120,150,0.4)', display: 'flex' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#f87171')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(100,120,150,0.4)')}
            >
              <Trash2 size={13} />
            </button>
            <button
              data-testid="r2-chat-expand-btn"
              onClick={toggleExpand}
              title={isExpanded ? 'Collapse' : 'Expand'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 6, color: 'rgba(100,120,150,0.4)', display: 'flex' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#5eead4')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(100,120,150,0.4)')}
            >
              {isExpanded ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            </button>
            <button
              data-testid="r2-chat-close-btn"
              onClick={closePanel}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 6, color: 'rgba(100,120,150,0.4)', display: 'flex' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#f87171')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(100,120,150,0.4)')}
            >
              <X size={13} />
            </button>
          </div>
        </div>

        {/* Context Bar */}
        <ContextBar />

        {/* ── Tab bar: Chat | History | Voice ──────────────────────────── */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(37,181,168,0.1)', background: 'rgba(2,5,14,0.9)', flexShrink: 0 }}>
          {([
            { key: 'chat'    as const, label: 'Chat',    icon: <MessageSquare size={11}/> },
            { key: 'history' as const, label: 'AI History', icon: <History size={11}/> },
            { key: 'voice'   as const, label: 'Voice',   icon: <Radio size={11}/> },
          ]).map(tab => (
            <button
              key={tab.key}
              data-testid={`chat-panel-tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '7px 6px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: activeTab === tab.key ? 700 : 500, color: activeTab === tab.key ? '#5eead4' : 'rgba(100,130,170,0.5)', borderBottom: `2px solid ${activeTab === tab.key ? '#5eead4' : 'transparent'}`, marginBottom: '-1px', transition: 'all 0.12s' }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* ── Voice panel ───────────────────────────────────────────────── */}
        {activeTab === 'voice' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <R2VoiceAssistant
              compact={false}
              onNavigate={route => { onNavigate(route); }}
              onChatSend={text => { setActiveTab('chat'); handleSend(text); }}
            />
          </div>
        )}

        {/* ── AI History panel ──────────────────────────────────────────── */}
        {activeTab === 'history' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <R2AIAssistHistory compact={false} />
          </div>
        )}

        {/* Messages area */}
        <div style={{
          flex: activeTab === 'chat' ? 1 : 0, overflowY: activeTab === 'chat' ? 'auto' : 'hidden',
          height: activeTab === 'chat' ? undefined : 0,
          padding: activeTab === 'chat' ? '14px 14px 0' : 0,
          scrollbarWidth: 'thin', scrollbarColor: 'rgba(37,181,168,0.2) transparent',
        }}>
          {/* Starter prompts when empty */}
          {messages.length === 0 && showStarters && (
            <div style={{ paddingBottom: 10 }}>
              <div style={{ textAlign: 'center', marginBottom: 18, paddingTop: 10 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%', margin: '0 auto 12px',
                  background: 'linear-gradient(135deg, rgba(37,181,168,0.15), rgba(37,181,168,0.04))',
                  border: '1px solid rgba(37,181,168,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Bot size={22} style={{ color: '#5eead4' }} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#f0f4ff', marginBottom: 4 }}>R2 is ready</div>
                <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.5)', fontFamily: 'sans-serif', lineHeight: 1.5 }}>
                  Your senior BIM coordinator, project manager,<br/>and engineering assistant — available 24/7.
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {STARTER_PROMPTS.map((prompt, i) => (
                  <button
                    key={i}
                    data-testid={`chat-starter-${i}`}
                    onClick={() => handleSend(prompt)}
                    style={{
                      padding: '8px 10px', borderRadius: 9, cursor: 'pointer', textAlign: 'left',
                      background: 'rgba(8,16,36,0.7)', border: '1px solid rgba(37,181,168,0.12)',
                      color: 'rgba(148,163,184,0.7)', fontSize: 11, fontFamily: 'sans-serif',
                      lineHeight: 1.4, transition: 'all 0.12s',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(37,181,168,0.06)';
                      (e.currentTarget as HTMLElement).style.color = '#c8d8f0';
                      (e.currentTarget as HTMLElement).style.border = '1px solid rgba(37,181,168,0.22)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(8,16,36,0.7)';
                      (e.currentTarget as HTMLElement).style.color = 'rgba(148,163,184,0.7)';
                      (e.currentTarget as HTMLElement).style.border = '1px solid rgba(37,181,168,0.12)';
                    }}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              msg={msg}
              onAction={handleAction}
              onCopy={handleCopy}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area — only show in Chat tab */}
        {activeTab === 'chat' && <div style={{
          padding: '10px 12px 14px', borderTop: '1px solid rgba(37,181,168,0.1)', flexShrink: 0,
          background: 'rgba(2,5,14,0.85)',
        }}>
          {/* Typing indicator */}
          {isStreaming && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, paddingLeft: 4 }}>
              <div style={{ display: 'flex', gap: 3 }}>
                {[0, 1, 2].map((i) => (
                  <div key={i} style={{
                    width: 4, height: 4, borderRadius: '50%', background: '#5eead4',
                    animation: `r2-dot 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }} />
                ))}
              </div>
              <span style={{ fontSize: 10, color: 'rgba(94,234,212,0.5)', fontFamily: 'sans-serif' }}>R2 is thinking...</span>
            </div>
          )}

          <div style={{
            display: 'flex', gap: 8, alignItems: 'flex-end',
            background: 'rgba(8,16,36,0.8)',
            border: `1px solid ${isListening ? 'rgba(248,113,113,0.4)' : 'rgba(37,181,168,0.2)'}`,
            borderRadius: 13, padding: '8px 10px',
            transition: 'border-color 0.2s',
          }}>
            <textarea
              ref={inputRef}
              data-testid="r2-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isListening ? 'Listening...' : 'Ask R2 anything about your project…'}
              rows={1}
              style={{
                flex: 1, background: 'none', border: 'none', outline: 'none',
                color: '#e2e8f0', fontSize: 12.5, fontFamily: 'sans-serif', resize: 'none',
                lineHeight: 1.5, maxHeight: 100, overflowY: 'auto',
              }}
            />
            <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
              <button
                data-testid="r2-chat-voice-btn"
                onClick={toggleVoice}
                title="Voice input"
                style={{
                  padding: 6, borderRadius: 8, cursor: 'pointer', border: 'none',
                  background: isListening ? 'rgba(248,113,113,0.15)' : 'none',
                  color: isListening ? '#f87171' : 'rgba(100,120,150,0.45)',
                  display: 'flex', transition: 'all 0.15s',
                }}
              >
                {isListening ? <MicOff size={14}/> : <Mic size={14}/>}
              </button>
              <button
                data-testid="r2-chat-send-btn"
                onClick={() => handleSend()}
                disabled={!input.trim() || isStreaming}
                style={{
                  padding: '5px 10px', borderRadius: 8, cursor: 'pointer', border: 'none',
                  background: input.trim() && !isStreaming ? 'rgba(37,181,168,0.2)' : 'rgba(30,40,60,0.3)',
                  color: input.trim() && !isStreaming ? '#5eead4' : 'rgba(100,120,150,0.3)',
                  display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700,
                  transition: 'all 0.15s', letterSpacing: '0.05em',
                }}
              >
                <Send size={13}/> Send
              </button>
            </div>
          </div>
          <div style={{ marginTop: 6, display: 'flex', gap: 10, justifyContent: 'center' }}>
            <span style={{ fontSize: 9, color: 'rgba(100,120,150,0.3)', fontFamily: 'sans-serif' }}>↵ send · Shift+↵ new line · Ctrl+Shift+R toggle</span>
          </div>
          {/* Ghost suggestion hint */}
          {ghostSuggestion && (
            <div
              data-testid="r2-ghost-suggestion"
              style={{
                marginTop: 5, padding: '5px 10px', borderRadius: 7,
                background: 'rgba(94,234,212,0.04)', border: '1px solid rgba(94,234,212,0.12)',
                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              }}
              onClick={() => { setInput(ghostSuggestion.text); setGhostSuggestion(null); inputRef.current?.focus(); }}
            >
              <span style={{ flex: 1, fontSize: 10.5, color: 'rgba(148,163,184,0.38)', fontFamily: 'sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic' }}>
                {ghostSuggestion.text}
              </span>
              <span style={{ fontSize: 8.5, color: 'rgba(94,234,212,0.35)', background: 'rgba(94,234,212,0.06)', border: '1px solid rgba(94,234,212,0.15)', padding: '1px 6px', borderRadius: 4, flexShrink: 0, fontFamily: 'monospace', fontWeight: 700 }}>
                Tab ↹
              </span>
            </div>
          )}
        </div>}
      </div>
      </FloatingWindowFrame>

      {/* Animations */}
      <style>{`
        @keyframes r2-panel-in {
          from { opacity: 0; transform: translateX(32px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes r2-cursor {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        @keyframes r2-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
        @keyframes r2-dot {
          0%, 100% { opacity: 0.3; transform: translateY(0); }
          50% { opacity: 1; transform: translateY(-3px); }
        }
      `}</style>
    </>
  );
}
